import React, { useEffect, useRef } from "react";
import { QP_AI_COLORS, playWhoosh } from "./qpAi";

// The QP AI unlock sequence, drawn on a canvas laid exactly over the world map:
// 1. every land dot lights up in a wave spreading out from the key's location,
// 2. the dots fly together into the QP logo,
// 3. then re-form as two hands pressed together - Vanakkam,
// 4. then scatter and fade away, and onDone() fires (the voice and the chat
//    come after, in FootprintMap).
const T = {
  wave: 2400,
  toLogo: 3800,
  logoHold: 5000,
  toHands: 6400,
  handsHold: 7800,
  scatter: 9000
};
const LOGO_SRC = "/assets/qp-logo.png";
const LOGO_RGB = [238, 106, 36]; // the Q mark's orange
const HANDS_RGB = [242, 169, 59]; // warm gold

const buildDots = ({ step, rows }) => {
  const dots = [];
  rows.split("|").forEach((row, r) => {
    if (!row) return;
    const y = (r + 0.5) * step;
    row.split(",").forEach((run) => {
      const [start, len] = run.split(".").map(Number);
      for (let i = 0; i < len; i++) dots.push({ x: (start + i + 0.5) * step, y });
    });
  });
  return dots;
};

const hexToRgb = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const mix = (a, b, t) => a.map((v, i) => Math.round(v + (b[i] - v) * t));
const ease = (p) => (p < 0.5 ? 4 * p * p * p : 1 - (-2 * p + 2) ** 3 / 2);
const clamp01 = (v) => Math.max(0, Math.min(1, v));
// Gold, lighter or deeper following the emoji's own shading.
const handShade = (pt) => {
  const k = 0.55 + 0.7 * (pt.shade ?? 0.7);
  return HANDS_RGB.map((v) => Math.min(255, Math.round(v * k)));
};

// Rasterise a shape (drawn by paint(ctx, size)) and return the filled cells as
// points in map units, centred on (cx, cy) and `span` units tall. Each point
// keeps the pixel's lightness as `shade`; with `dropDark`, near-black pixels
// (an emoji's outlines between fingers and palms) are left out so that detail
// shows through as gaps.
const sampleShape = (paint, cx, cy, span, step, dropDark = false) => {
  const size = 240;
  const off = document.createElement("canvas");
  off.width = size;
  off.height = size;
  const o = off.getContext("2d", { willReadFrequently: true });
  paint(o, size);
  const data = o.getImageData(0, 0, size, size).data;
  const cell = (step / span) * size;
  const pts = [];
  for (let y = cell / 2; y < size; y += cell) {
    for (let x = cell / 2; x < size; x += cell) {
      const i = (Math.floor(y) * size + Math.floor(x)) * 4;
      if (data[i + 3] <= 110) continue;
      const shade = (0.3 * data[i] + 0.59 * data[i + 1] + 0.11 * data[i + 2]) / 255;
      if (dropDark && shade < 0.28) continue;
      pts.push({ x: cx + ((x - size / 2) / size) * span, y: cy + ((y - size / 2) / size) * span, shade });
    }
  }
  return pts;
};

// Hand-drawn Vanakkam silhouette, used when the device can't render the emoji.
const paintHandsFallback = (o, s) => {
  o.save();
  o.scale(s / 100, s / 100);
  o.fillStyle = "#fff";
  o.beginPath();
  o.moveTo(50, 6);
  o.bezierCurveTo(58, 16, 66, 34, 68, 56);
  o.lineTo(64, 74);
  o.lineTo(36, 74);
  o.lineTo(32, 56);
  o.bezierCurveTo(34, 34, 42, 16, 50, 6);
  o.fill();
  o.fillRect(28, 78, 44, 14);
  // the join between the palms and the thumbs, cut back out
  o.globalCompositeOperation = "destination-out";
  o.fillRect(49.2, 10, 1.6, 62);
  o.beginPath();
  o.moveTo(50, 44);
  o.quadraticCurveTo(43, 52, 44, 66);
  o.moveTo(50, 44);
  o.quadraticCurveTo(57, 52, 56, 66);
  o.lineWidth = 1.6;
  o.stroke();
  o.restore();
};

const paintHandsEmoji = (o, s) => {
  o.textAlign = "center";
  o.textBaseline = "middle";
  o.font = `${Math.round(s * 0.82)}px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif`;
  o.fillText("\u{1F64F}", s / 2, s / 2 + s * 0.04);
};

const QpAiIntro = ({ grid, width, height, origin, onDone }) => {
  const canvasRef = useRef(null);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    let raf;
    let cancelled = false;
    const canvas = canvasRef.current;
    // Layout size, so a rotated (phone) fullscreen map is measured correctly.
    const box = { width: canvas.parentElement.clientWidth, height: canvas.parentElement.clientHeight };
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(box.width * dpr);
    canvas.height = Math.round(box.height * dpr);
    const c = canvas.getContext("2d");
    c.scale((box.width * dpr) / width, (box.width * dpr) / width);

    const dark = document.documentElement.classList.contains("dark");
    const palette = (dark ? QP_AI_COLORS.dark : QP_AI_COLORS.light).map(hexToRgb);
    const base = dark ? [120, 120, 120] : [150, 150, 150];
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const dots = buildDots(grid).map((d) => {
      const dx = d.x - origin.x;
      const dy = d.y - origin.y;
      const dist = Math.hypot(dx, dy) || 1;
      return { ...d, dist, jitter: Math.random() };
    });
    const maxDist = Math.max(...dots.map((d) => d.dist));
    dots.forEach((d) => {
      const t = Math.min(d.dist / maxDist, 0.999) * (palette.length - 1);
      const i = Math.floor(t);
      d.rgb = mix(palette[i], palette[i + 1], t - i);
    });

    // Shapes, centred in the map box, ~70% of its height.
    const cx = width / 2;
    const cy = height / 2;
    const span = height * 0.72;
    const step = grid.step * 0.95;

    const start = (logoImg) => {
      if (cancelled) return;
      const logoPts = logoImg
        ? sampleShape((o, s) => o.drawImage(logoImg, s * 0.04, s * 0.04, s * 0.92, s * 0.92), cx, cy, span, step)
        : [];
      let handPts = sampleShape(paintHandsEmoji, cx, cy, span, step, true);
      // A missing emoji font draws an empty "tofu" box - far fewer filled cells.
      if (handPts.length < 800) handPts = sampleShape(paintHandsFallback, cx, cy, span, step);

      // Give each shape point a dot; order both by angle round the centre so the
      // swarm flows rather than criss-crossing. Unused dots fade out.
      const byAngle = (p) => Math.atan2(p.y - cy, p.x - cx);
      const order = [...dots].sort((a, b) => byAngle(a) - byAngle(b));
      const assign = (pts, key) => {
        const sorted = [...pts].sort((a, b) => byAngle(a) - byAngle(b));
        const n = Math.min(sorted.length, order.length);
        const stride = order.length / n;
        for (let i = 0; i < n; i++) order[Math.floor(i * stride)][key] = sorted[i];
      };
      assign(logoPts, "logo");
      assign(handPts, "hands");
      dots.forEach((d) => {
        const a = Math.random() * Math.PI * 2;
        const speed = 25 + Math.random() * 70;
        d.vx = Math.cos(a) * speed;
        d.vy = Math.sin(a) * speed;
      });

      let whooshed = false;
      const t0 = performance.now();
      const k = reduced ? 0.15 : 1; // reduced motion: same beats, much shorter

      const frame = (now) => {
        const t = (now - t0) / k;
        c.clearRect(0, 0, width, height);

        const dot = (x, y, r, rgb, a) => {
          if (a <= 0.01) return;
          c.beginPath();
          c.arc(x, y, r, 0, Math.PI * 2);
          c.fillStyle = `rgba(${rgb.join(",")},${a})`;
          c.fill();
        };

        if (t < T.wave) {
          const front = (t / T.wave) * maxDist * 1.15;
          for (let i = 0; i < 3; i++) {
            const r = front - i * 14;
            if (r <= 0) continue;
            c.beginPath();
            c.arc(origin.x, origin.y, r, 0, Math.PI * 2);
            c.strokeStyle = `rgba(${palette[1].join(",")},${0.35 - i * 0.1})`;
            c.lineWidth = 0.35;
            c.stroke();
          }
          for (const d of dots) {
            const lit = Math.exp(-((d.dist - front) ** 2) / (2 * 7 ** 2));
            const after = d.dist < front ? 0.55 + 0.15 * Math.sin(t / 140 + d.jitter * 6) : 0;
            const glow = Math.max(lit, after);
            dot(d.x, d.y, 0.45 + lit * 0.55, glow > 0 ? mix(base, d.rgb, Math.min(1, glow * 1.4)) : base, 0.35 + glow * 0.65);
          }
        } else if (t < T.handsHold) {
          // Map -> logo -> hands.
          const pLogo = ease(clamp01((t - T.wave) / (T.toLogo - T.wave)));
          const pHands = ease(clamp01((t - T.logoHold) / (T.toHands - T.logoHold)));
          const shimmer = 0.85 + 0.15 * Math.sin(t / 120);
          for (const d of dots) {
            const from = { x: d.x, y: d.y };
            const L = d.logo || null;
            const H = d.hands || null;
            let x = from.x;
            let y = from.y;
            let rgb = d.rgb;
            let a = 0.9;
            // Stage 1: to the logo (or fade if unused by it).
            if (L) {
              x += (L.x - from.x) * pLogo;
              y += (L.y - from.y) * pLogo;
              rgb = mix(d.rgb, LOGO_RGB, pLogo);
            } else {
              a *= 1 - pLogo;
            }
            // Stage 2: to the hands (dots unused by the logo fade back in if needed).
            if (pHands > 0) {
              if (H) {
                const sx = L ? L.x : from.x;
                const sy = L ? L.y : from.y;
                x = sx + (H.x - sx) * pHands;
                y = sy + (H.y - sy) * pHands;
                rgb = mix(L ? LOGO_RGB : d.rgb, handShade(H), pHands);
                a = L ? 0.9 : 0.9 * pHands;
              } else if (L) {
                a = 0.9 * (1 - pHands);
              }
            }
            dot(x, y, 0.62, rgb, a * (t > T.toLogo ? shimmer : 1));
          }
        } else if (t < T.scatter) {
          if (!whooshed) {
            whooshed = true;
            if (!reduced) playWhoosh();
          }
          const p = clamp01((t - T.handsHold) / (T.scatter - T.handsHold));
          const e = 1 - (1 - p) ** 3;
          for (const d of dots) {
            if (!d.hands) continue;
            dot(d.hands.x + d.vx * e, d.hands.y + d.vy * e, 0.62 * (1 - p * 0.6), handShade(d.hands), Math.max(0, 1 - p * (1.1 + d.jitter * 0.6)));
          }
        } else {
          doneRef.current?.();
          return;
        }
        raf = requestAnimationFrame(frame);
      };
      raf = requestAnimationFrame(frame);
    };

    const img = new Image();
    img.onload = () => start(img);
    img.onerror = () => start(null);
    img.src = LOGO_SRC;

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [grid, width, height, origin]);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true" />;
};

export default QpAiIntro;
